/** Which one action the robot bubble offers (`BubbleTaskAction`). */
import type { TaskState } from '../../../api/rmf-client';
import type { TaskControlGates } from '../../Tasks/task-control-gates';
import { isTerminalTaskStatus } from '../../Tasks/task-ownership';

export type BubbleAction = 'interrupt' | 'resume' | 'cancel';

/**
 * The one action, or null. A job that is stuck can only want cancelling: one
 * flagged for attention, or a finished record a robot is still executing.
 * (`held` alone is not stuck: any robot running its own job holds it.) A
 * healthy one wants pausing or resuming, by its status. Only what the gates
 * offer is ever offered, so a robot held after a fire alarm gets no Resume.
 */
export function bubbleActionFor(
  task: Pick<TaskState, 'status' | 'attention'>,
  gates: Pick<TaskControlGates, 'availableActions' | 'held' | 'inLift'>,
): BubbleAction | null {
  const has = (action: BubbleAction) => gates.availableActions.some((a) => a.action === action);
  const stuck = Boolean(task.attention) || (gates.held && isTerminalTaskStatus(task.status));
  if (stuck) return has('cancel') ? 'cancel' : null;
  // Not while it is in or at a car (Akash, 2026-11-02): a paused robot holding
  // a car keeps it from guests, and a map is no place to find that out. The
  // panel still offers it, with the rest of what is true about the ride.
  if (task.status === 'active') return has('interrupt') && !gates.inLift ? 'interrupt' : null;
  if (task.status === 'interrupted') return has('resume') ? 'resume' : null;
  return null;
}
